import type { RunFailureReason, SpillCause } from "./events";

/**
 * GridSnapshot — the authoritative, serializable state of a stage at a point
 * in time. This is the ONLY thing the renderer is allowed to draw from.
 *
 * Design rule: a snapshot is plain data (no methods, no class instances) so
 * it can be produced by the real engine, produced by a scripted mock, sent
 * over the wire, or asserted against in a test with a plain deep-equal.
 */

/** A chemical species quantity map, e.g. `{ HCl: 1, H2O: 2 }`. Integer units only. */
export type Composition = Record<string, number>;

/**
 * The 7-category hardware taxonomy of `docs/design/00-pillars.md` §4 and
 * `01-simulation-model.md` §2, plus the three I/O kinds that belong to no
 * category. `phaseConverter` and `incubator` became live in
 * `docs/specs/implemented/0003-phase-converter-and-incubator-nodes.md` — the
 * first splits a packet into vapour and liquid fractions, the second holds one
 * for a configured dwell. `thermalRegulator` is inert except that §3.4's
 * heatwave leaves it and `flowMover` unfrozen. `separator` became live in
 * `docs/specs/implemented/0023-separator-and-hopper-roles.md`, which also
 * split the single collection kind that preceded it into `productHopper`
 * (scores the objective) and `wasteBuyer` (collects byproducts, scores
 * nothing — GAPS.md §9.1 Q6, Q8).
 */
export type NodeKind =
  | "inputTank"
  | "productHopper"
  /** Accepts byproducts for later valorization (`0026`) or a dumping fine; scores nothing itself. */
  | "wasteBuyer"
  | "reactor"
  | "thermalRegulator"
  | "separator"
  | "phaseConverter"
  | "catalyticBed"
  | "incubator"
  | "flowMover"
  | "flowBuffer"
  /**
   * Spec 0032: a flare sink. Collects whatever reaches it and pays nothing —
   * legal disposal, fined below the `wasteBuyer` dumping rate rather than
   * valorized. Terminal like both hoppers; never pushes a packet back out.
   */
  | "vent";

/** The 7 categories of `00-pillars.md` §4. One category, one or more node kinds. */
export type HardwareCategory =
  | "reactor"
  | "thermalRegulator"
  | "separator"
  | "phaseConverter"
  | "catalyticBed"
  | "incubator"
  | "flowMoverBuffer";

/**
 * Maps each `NodeKind` to its hardware category. `null` for the four I/O
 * kinds (`inputTank`, `productHopper`, `wasteBuyer`, `vent`), which belong to
 * no category. `flowMover` and `flowBuffer` share the `flowMoverBuffer`
 * category but stay distinct kinds — merging them would erase `splitCursor`
 * round-robin routing, two meshes, and two cards.
 */
export const CATEGORY_OF_NODE_KIND: Record<NodeKind, HardwareCategory | null> = {
  inputTank: null,
  productHopper: null,
  wasteBuyer: null,
  vent: null,
  reactor: "reactor",
  thermalRegulator: "thermalRegulator",
  separator: "separator",
  phaseConverter: "phaseConverter",
  catalyticBed: "catalyticBed",
  incubator: "incubator",
  flowMover: "flowMoverBuffer",
  flowBuffer: "flowMoverBuffer",
};

/**
 * A balanced reaction a node kind can run. Lives in `protocol/` (rather than
 * `engine/definitions.ts`, where it used to sit alone) because the stage's
 * recipe list is part of the snapshot now — the inspector reads the same
 * `RecipeDef` the engine fires from, never a re-typed copy. `engine/definitions.ts`
 * re-exports this so stage authors keep importing it from there.
 */
export interface RecipeDef {
  readonly id: string;
  /** Consumed from the node's buffer when the recipe fires. */
  readonly inputs: Composition;
  /** Produced as a single new packet. */
  readonly outputs: Composition;
  readonly isExothermic: boolean;
  /** Heat counters added to the node when this fires. */
  readonly heat: number;
  /** Node kinds able to run this recipe. */
  readonly nodeKinds: readonly NodeKind[];

  /**
   * phaseConverter only: integer °C at or above which this stream's volatiles
   * flash off. May be negative (NH3 boils at -33 °C). Requires
   * `volatileSpecies` (spec 0003, revision 2026-09-14). A recipe carrying
   * this (or `holdDuration`, below) is a "property recipe" — it describes how
   * a stream behaves and never fires as a reaction; `recipesFor` in
   * `reactions.ts` filters it out of the normal reaction-firing pass.
   */
  readonly boilingPoint?: number;
  /** phaseConverter only: which of this recipe's `inputs` species leave as vapour. */
  readonly volatileSpecies?: readonly string[];
  /**
   * incubator only: N consecutive ticks a packet of this stream holds before
   * release. When `outputs` differs from `inputs`, the hold rewrites the
   * packet's composition in place on completion (`composition - k·inputs +
   * k·outputs`, `k = maxMultiple(packet, inputs)`) and emits `reaction.fired`
   * with `isExothermic: false` — a transforming hold (spec 0025). It is still
   * a property recipe and never fires in `processReactions`.
   */
  readonly holdDuration?: number;
  /**
   * incubator only, requires `holdDuration`: integer °C at or above which a
   * packet's hold accrues. Below it the packet is not held and leaves
   * untransformed on its next movement step (spec 0025).
   */
  readonly minTemperature?: number;
  /**
   * separator only: the species that leave down the `main` edge. Everything
   * else in the pooled `k * inputs` partition leaves down `waste` (spec 0023,
   * `01-simulation-model.md` §4). A recipe carrying this is a property
   * recipe, like `boilingPoint`/`holdDuration` — it never fires as a reaction.
   */
  readonly primarySpecies?: readonly string[];
}

/**
 * What an attachment does once snapped onto a node. Lives here for the same
 * reason as `RecipeDef`: a fitted attachment's effect is snapshot data, and
 * `engine/definitions.ts` re-exports it for card authors.
 *
 * Those that simply raise a stat do exactly that on attach — insulation moves
 * criticalTemp, a surge drum moves bufferCapacity — so the change is visible
 * in the snapshot and every rule that reads the stat honours it for free.
 * Only a guard bed needs state of its own, because it is consumed.
 */
export type AttachmentEffect =
  | { readonly kind: "coolingJacket"; readonly heatShedPerTick: number }
  | { readonly kind: "ptfeLining" }
  | { readonly kind: "burstDisk"; readonly extraPressureTolerance: number }
  /** §2.4's named mitigation: soaks up impure packets before the bed does. */
  | { readonly kind: "guardBed"; readonly charges: number }
  | { readonly kind: "insulation"; readonly extraCriticalTemp: number }
  | { readonly kind: "surgeDrum"; readonly extraBufferCapacity: number }
  /**
   * §3.3's named mitigation for an unsealed waste hopper (spec 0023, Open
   * Question 3): a `wasteBuyer` wearing one is exempt from the per-turn
   * `verminRisk` accrual `engine/facility.ts` charges an unsealed one. Carries
   * no stat of its own — read live off the attachment list, like a cooling
   * jacket or burst disk.
   */
  | { readonly kind: "lid" };

/**
 * An attachment as fitted to a node. Attachment kind is not one-to-one with a
 * card — Cooling Jacket and Twin Cooling Jacket are both `coolingJacket`,
 * Guard Bed and Polishing Filter are both `guardBed` — so naming it honestly
 * for the inspector needs the id of the card that fitted it, resolved through
 * the `@reagent/content` catalogue the way a hand card's `cardId` already is.
 */
export interface FittedAttachment {
  readonly cardId: string;
  readonly effect: AttachmentEffect;
}

/**
 * `"setup"` is `00-pillars.md` §3's Phase 1: Workshop Setup, a frozen-time
 * phase before turn 1 (spec 0017). It precedes `"draw"` exactly once per
 * stage — `advancePhase` never re-enters it.
 */
export type GamePhase = "setup" | "draw" | "play" | "run" | "status";

/**
 * One thing a stage's Phase 1 catalogue sells (spec 0017). Stage data, like
 * `RecipeDef` — the ₵ price is authored per stage, never derived by the
 * renderer. `costCredits = costKw * 5` is the authoring rule confirmed by the
 * owner (0017 Open Question 1, 2026-09-19); it still needs playtest
 * confirmation of the multiplier itself, not the mechanism.
 */
export interface SetupOffer {
  readonly cardId: string;
  readonly costCredits: number;
}

/**
 * Spec 0067: what live building (install, dismantle, move and clean during
 * Play) costs on a stage. Every number is a PLACEHOLDER needing playtest. A
 * stage that authors no `liveBuild` keeps Play-phase building closed.
 */
export interface LiveBuildRule {
  readonly installLabourKw: number;
  readonly dismantleLabourKw: number;
  readonly moveLabourKw: number;
  readonly moveCostCredits: number;
  /** Applied to the resale-ladder refund of a clean unit dismantled in Play. 0..100. */
  readonly playRefundPct: number;
  readonly cleanFeeCredits: number;
  /** Follow-up hook (Maintenance Contracts): loaded moves per turn that lose nothing and spill nothing. */
  readonly spillFreeMovesPerTurn: number;
}

/**
 * One physical card the player owns. Two copies of `card-cooling-jacket` are
 * two instances with the same `cardId`, which is what lets one be struck,
 * played or discarded without touching the other (spec 0016, closing
 * `GAPS.md` G-F7 — Clogged Line's locked tile used to be found by hand
 * position, which a destroyed source node could silently shift onto an
 * unrelated tile).
 */
export interface CardInstance {
  /** Stable for the life of the card, across stages and saves. */
  readonly instanceId: string;
  readonly cardId: string;
  /** Run stage index the card was acquired at; the resale ladder reads it (0021). */
  readonly acquiredStageIndex: number;
  /** The node this card is bound to, when it was injected by hardware (0020). */
  readonly boundNodeId: string | null;
  /** 02-cards-and-economy.md §3: a contaminated asset resells for nothing. */
  readonly isContaminated: boolean;
}

/**
 * A tile the grid has marked contaminated (03-hazards.md §3.1, spec 0005).
 * `sourceId` is the edge or node id that spilled here, carried for the
 * inspector's attribution line (§1's "Explicit Neglect & Incident
 * Attribution") — never re-derived from position.
 */
export interface ContaminatedTileState {
  readonly pos: GridPos;
  readonly cause: SpillCause;
  readonly sourceId: string;
  readonly sinceTurn: number;
}

/** Axial/offset grid coordinate. The renderer owns the projection to world space. */
export interface GridPos {
  readonly x: number;
  readonly y: number;
}

/** 0/90/180/270 — rotation of a hardware tile on the grid, degrees clockwise. */
export type Rotation = 0 | 90 | 180 | 270;

/**
 * A tank's two supply contracts (spec 0027). Moved here from
 * `engine/definitions.ts` — re-exported from there for stage/card authors —
 * because `NodeState.sourcing` below carries it into the snapshot.
 */
export type PrecursorTier = "budget" | "premium";

/**
 * A tank's two supply contracts. `costCredits` is a property of the supplier,
 * not of the tank, so the later town overworld can derive it from map position
 * and transport (GAPS.md §11.4, P5) without moving it.
 */
export interface SourcingDef {
  readonly budget: {
    /** Units added to every emitted packet. `Sludge` by convention (Q15); any other species is a side-product. */
    readonly impurities: Composition;
    readonly costCredits: number;
  };
  readonly premium: {
    /** Total units this line may emit all stage. Uncapped when absent. */
    readonly supplyCap?: number;
    readonly costCredits: number;
  };
}

/** 03-hazards.md §3.3 (spec 0033). */
export interface InfestationState {
  /** Turn the rodents arrived; that turn never counts toward starving them out. */
  readonly sinceTurn: number;
  /** The node loses 1 tick in this many: 2, or 4 under Facility Mousetrap. */
  readonly lostTickPeriod: number;
}

export interface NodeState {
  readonly id: string;
  readonly kind: NodeKind;
  readonly pos: GridPos;
  readonly rotation: Rotation;

  /** Packets currently held in the node's internal buffer, awaiting processing/exit. */
  readonly buffer: readonly PacketState[];
  readonly bufferCapacity: number;

  /** inputTank only: what it emits each tick. Null everywhere else. */
  readonly feed: Composition | null;

  /**
   * inputTank with authored `sourcing` only; `null` otherwise (spec 0027). A
   * tank without `sourcing` needs no contract — it is `{ tier: "premium" }`,
   * free and uncapped, and reports `null` here rather than a synthesised
   * choice. `tier` is `null` until `setPrecursor` picks one.
   */
  readonly sourcing: { readonly tier: PrecursorTier | null; readonly options: SourcingDef } | null;
  /**
   * inputTank only (spec 0027): units left before a capped Premium line runs
   * dry. `null` when uncapped (Budget, or a Premium line authored with no
   * `supplyCap`) or when `sourcing.tier` is still unchosen.
   */
  readonly supplyRemaining: number | null;

  /** Attachments fitted to this node, in the order they were fitted. */
  readonly attachments: readonly FittedAttachment[];
  /** Remaining guard-bed / polishing-filter charges (`03-hazards.md` §2.4). */
  readonly guardCharges: number;

  /** Hazard counters — always present, zero when not applicable to this node kind. */
  readonly heatCounter: number;
  readonly criticalTemp: number;
  readonly isOverheatWarning: boolean;
  /** Consecutive ticks spent at or above criticalTemp without being cooled. */
  readonly overheatTicks: number;

  readonly catalystHealth: number; // 0-100, only meaningful for catalyticBed
  readonly residenceTicks: number; // consecutive ticks current buffer contents haven't moved
  readonly isSludged: boolean;
  /** Spec 0035: Low Fire's cap on this node's `k` this turn, or `null` when uncapped. */
  readonly maxMultipleThisTurn: number | null;

  /**
   * incubator: hold duration resolved for the current buffer against the
   * matching property recipe; 0 when no recipe matches or for every other
   * kind (spec 0003, revision 2026-09-14 — resolved per tick, not stored
   * config).
   */
  readonly holdTicks: number;
  /**
   * phaseConverter: boiling point resolved for the current buffer against the
   * matching property recipe; null when no recipe matches or for every other
   * kind (spec 0003, revision 2026-09-14 — resolved per tick, not stored
   * config).
   */
  readonly phaseThreshold: number | null;

  readonly isDestroyed: boolean;

  /**
   * 03-hazards.md §3.3 (spec 0033): `null` when not infested. Only ever
   * non-null on an infestable kind (`CATEGORY_OF_NODE_KIND[kind] !== null`).
   */
  readonly infestation: InfestationState | null;

  /**
   * flowMover only: the player-owned routing setting (spec 0024). `null` for
   * every other kind and for a mover the stage left on automatic
   * round-robin.
   */
  readonly valveState: ValveState | null;

  /**
   * Spec 0031: the node's bound recipe ids, or `null` when it selects by
   * kind alone (every recipe kind-matches, today's behaviour for every node
   * before this spec).
   */
  readonly recipeIds: readonly string[] | null;
}

/**
 * flowMover only (spec 0024): a player-set routing mode, persisted as state
 * rather than replayed every turn like a card. `open` behaves exactly like
 * `null` (round-robin by `splitCursor`); `closed` moves nothing; `route`
 * replaces the cursor with one named outgoing edge, which blocks — rather
 * than falling back — when that edge is full.
 */
export type ValveState =
  | { readonly kind: "open" }
  | { readonly kind: "closed" }
  /** Route everything to one named downstream edge instead of round-robin. */
  | { readonly kind: "route"; readonly edgeId: string };

export interface EdgeState {
  readonly id: string;
  readonly fromNodeId: string;
  readonly toNodeId: string;
  /** World-space waypoints for rendering the pipe mesh; grid-adjacent edges may have just 2. */
  readonly path: readonly GridPos[];

  readonly material: "glass" | "ptfeLined" | "hastelloy";
  readonly hp: number;
  readonly maxHp: number;
  readonly pressureCounter: number;
  /**
   * The pressure counter this edge bursts at — `PRESSURE_BURST_THRESHOLD` plus
   * any tolerance a Burst Disk on the fed node buys it. Computed here so the
   * renderer never re-derives it from the node's attachments itself.
   */
  readonly burstLimit: number;
  readonly isOverpressured: boolean;
  readonly isLeaking: boolean;
  readonly isBurst: boolean;
  /** phaseConverter outputs only: which fraction this edge carries (spec 0003). */
  readonly phaseRole: "vapour" | "liquid" | null;
  /** separator outputs only: which fraction this edge carries (spec 0023), mirrors `phaseRole`. */
  readonly separationRole: "main" | "waste" | null;
}

export interface PacketState {
  readonly id: string;
  readonly composition: Composition;
  /** Purity of the dominant species, 0-100, used for catalyst poisoning checks. */
  readonly purity: number;
  /**
   * Integer °C, per `01-simulation-model.md` §3. Read by the phase converter's
   * volatility threshold and carried onto both output fractions (spec 0003). A
   * feed is born at `AMBIENT_TEMP_C` unless its tank sets `feedTemperature`,
   * and only a `thermalRegulator` with a configured `deltaTemp` changes it
   * after that — no ambient drift in transit or at rest (spec 0022).
   */
  readonly temperature: number;
  /** Consecutive ticks in the current node buffer without moving. */
  readonly residenceTicks: number;
  /** Carries sludge, dust, or unwanted side-product species. */
  readonly isContaminated: boolean;
  /** Where the packet currently is: travelling an edge, or resting in a node buffer. */
  readonly location:
    | { readonly kind: "onEdge"; readonly edgeId: string; readonly t: number } // t: 0..1 progress
    | { readonly kind: "inNode"; readonly nodeId: string };
}

/**
 * §3.2 and §3.4 (spec 0006): externally authored facility events, fired by
 * turn index against `EngineState.turn`. `"powerSurge"` is the only kind this
 * spec's engine code dispatches — the overload branch of §3.2 is deferred to
 * `0007`, but the literal lives on `PollutionReason.cause` there, not here.
 */
export type ScriptedEventKind = "powerSurge" | "heatwave" | "seismic";

export interface ScriptedEventDef {
  /** 1-based, compared against `EngineState.turn`. Fires once, ever. */
  readonly turn: number;
  readonly kind: ScriptedEventKind;
}

/**
 * Spec 0028: one Phase 1 facility mitigation bought this stage — an answer
 * installed, not merely offered (`setupCatalogue` is what's for sale;
 * `FacilityMitigationKind`/the full effect payload live on the engine's
 * `CardDef.facility`, resolved through the content catalogue by `cardId`, the
 * same exception `NodeState.attachments` does not need since a facility
 * mitigation is facility-wide rather than per-node). `spent` is only ever
 * true for a one-shot (Hazmat Absorbent, Emergency Power Cell); a standing
 * mitigation (Surge Protector, Facility Mousetrap, HVAC Chillers, Seismic
 * Isolation Mounts) stays false for the life of the stage.
 */
export interface FacilityMitigationState {
  readonly cardId: string;
  readonly spent: boolean;
}

export interface ObjectiveState {
  readonly targetComposition: Composition;
  readonly collected: Composition;
  readonly minPurity: number;
}

/**
 * Run performance, per `02-cards-and-economy.md` §5 (Target Yield and Purity Rate
 * and a between-stages budget "earned from stage performance"). Always
 * present and always current, so the HUD can show a running total rather than
 * only a post-mortem.
 */
/**
 * A limit the coming turn will cross if the player plays nothing more
 * (spec 0018, `00-pillars.md` §3 Phase 2's Forecast sub-phase). Produced by
 * dry-running a clone of the engine — see `GridSnapshot.forecast` — never
 * hand-derived, so it cannot drift from the rules that actually fire.
 */
export interface ForecastEntry {
  /** 0-based tick within the coming turn. */
  readonly tickInTurn: number;
  readonly kind:
    | "overheatWarning"
    | "explosion"
    | "pressureBurst"
    | "leak"
    | "residenceSludge"
    | "catalystPoisoned";
  /** The node or edge it happens to. */
  readonly targetId: string;
}

/**
 * Run tallies — what happened, reported plainly. `0026` replaced the engine's
 * old points-total formula with this: the engine still has no notion of
 * Credits, but every number `@reagent/meta`'s `StagePayout` needs is a tally
 * the engine already keeps, named for what it counts rather than for how it
 * will be priced.
 */
export interface RunTallies {
  /** Units of the objective's species collected at a product hopper. */
  readonly scoredUnits: number;
  readonly targetUnits: number;
  /** Unit-weighted mean purity of everything that scored, 0-100. */
  readonly averagePurity: number;
  /** Sellable byproduct delivered to a waste buyer (`0023`). */
  readonly valorizedUnits: number;
  /** Material destroyed, leaked, or collected at a hopper below minPurity. */
  readonly wastedUnits: number;
  /** Spec 0032: units collected at a `vent`. Never in `valorizedUnits` or `wastedUnits`. */
  readonly ventedUnits: number;
  readonly turnsUsed: number;
  readonly maxTurns: number;
}

export interface GridSnapshot {
  readonly stageId: string;
  /** Human-readable stage name, for the job sheet. */
  readonly stageName: string;
  /** Board size. Stages differ, and the renderer has to build the right table. */
  readonly cols: number;
  readonly rows: number;
  readonly turn: number;
  readonly phase: GamePhase;

  /**
   * Progress through the current Run phase. The UI needs both: to show the
   * player how much of the turn is left, and to know when the tick clock has
   * nothing more to do and the turn should roll over. Zero outside Run.
   */
  readonly tickInTurn: number;
  readonly ticksPerTurn: number;
  readonly maxTurns: number;

  readonly energyBudgetKw: number;
  readonly energySpentKw: number;

  /**
   * Phase 1's own currency (spec 0017). Composed in by `@reagent/meta` from
   * `RunState.credits` through `composeStage`; the engine spends a budget and
   * never learns where it came from or what is left after, the same
   * separation `energyBudgetKw` already keeps. Zero and empty on every
   * shipped stage until `0037` rebalances them — see `setupCatalogue`.
   */
  readonly setupBudgetCredits: number;
  readonly setupSpentCredits: number;
  /** What this stage's Phase 1 sells, with ₵ prices. Stage data, like `recipes`. */
  readonly setupCatalogue: readonly SetupOffer[];
  /** Spec 0028: this stage's installed Phase 1 facility mitigations — see `FacilityMitigationState`. */
  readonly facilityMitigations: readonly FacilityMitigationState[];
  /** Spec 0067: null when the stage authors no `liveBuild` — Play-phase building is closed. */
  readonly liveBuild: LiveBuildRule | null;
  /** Spec 0067: spill-free loaded moves still available this turn. 0 outside Play. */
  readonly spillFreeMovesLeft: number;

  readonly nodes: readonly NodeState[];
  readonly edges: readonly EdgeState[];
  /** Packets not resolved into a node buffer entry above, i.e. actively in flight. */
  readonly packetsInFlight: readonly PacketState[];

  /** 03-hazards.md §3.1 (spec 0005). Sorted by `(y, x)` for stable iteration. */
  readonly contaminatedTiles: readonly ContaminatedTileState[];
  /** 03-hazards.md §3.3 (spec 0005): the facility-wide pest counter. */
  readonly verminRisk: number;

  /**
   * 03-hazards.md §3.2/§3.4 (spec 0006): the stage's still-pending scripted
   * events, in authored order — shrinks as each fires (`firedScriptedEvents`
   * on `EngineState`), so the HUD can telegraph "Turn N: <event>" ahead of
   * time per Pillar 1 (Legible Causality) without importing stage data.
   */
  readonly upcomingScriptedEvents: readonly ScriptedEventDef[];
  /** §3.2: ticks remaining on a scripted power surge's hardware freeze. */
  readonly powerFreezeTicks: number;
  /** §3.4: true from a heatwave's trigger turn to the end of the stage. */
  readonly isHeatwaveActive: boolean;

  readonly objective: ObjectiveState;
  readonly tallies: RunTallies;

  readonly hand: readonly CardInstance[];
  readonly drawPileCount: number;
  readonly discardPileCount: number;
  /**
   * Draw + hand + discard, in stable order — the deck as it stands (spec
   * 0007). `@reagent/meta` reconciles injected cards into `RunState.deck`
   * from this at stage end, which is what makes §3's Strike fee reach an
   * actual hazard card for the first time. The engine still knows nothing
   * about runs or Credits.
   */
  readonly deckCards: readonly CardInstance[];

  /**
   * Every hazard-card penalty currently in force (spec 0007), so the HUD
   * never re-derives one from the hand and the two cannot disagree. All
   * integers; all zero/empty when the hand holds no hazard card.
   */
  readonly deckPenalties: {
    readonly powerPenaltyKw: number;
    readonly directiveSurchargeKw: number;
    readonly handSizePenaltyNextTurn: number;
    readonly lockedTiles: readonly GridPos[];
    /** Hand slots that cannot be discarded or refilled (Chemical Hazard Spill). */
    readonly lockedHandInstanceIds: readonly string[];
  };

  /** The stage's own recipes, so the inspector can read a machine's equations. */
  readonly recipes: readonly RecipeDef[];
  /** Species that eat standard pipe walls on this stage (`03-hazards.md` §2.3). */
  readonly corrosiveSpecies: readonly string[];

  /**
   * Global hazard limits, quoted once here rather than re-typed as constants
   * in the renderer — copies drift, a snapshot field cannot.
   */
  readonly limits: {
    /** `03-hazards.md` §2.1: uncooled ticks at/above criticalTemp before explosion. */
    readonly overheatGraceTicks: number;
    /** `03-hazards.md` §2.5: ticks a packet may sit in a buffer before it sludges. */
    readonly sludgeResidenceTicks: number;
    /** `03-hazards.md` §2.4: purity below this poisons a catalyst bed. */
    readonly catalystMinPurity: number;
    /** 03-hazards.md §3.3 (spec 0005): verminRisk at which rodents fire. */
    readonly verminRiskThreshold: number;
  };

  readonly isRunComplete: boolean;
  readonly isRunFailed: boolean;
  /** Set when isRunFailed — the end screen has to say which failure it was. */
  readonly failureReason: RunFailureReason | null;

  /**
   * `00-pillars.md` §3 Phase 2's Forecast (spec 0018): every hazard threshold
   * the coming turn will cross if the player plays nothing more, computed by
   * dry-running a clone of the engine on the real board — never touching real
   * state or the real RNG. Empty outside the Play phase.
   */
  readonly forecast: readonly ForecastEntry[];
}
